import type { IOmit } from '@sixui/ui';
import { Button, Dialog, IDialogProps, TextInputField, useId } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';
import { ContentBlock } from '~/components/molecules/ContentBlock';

export type IYouTubeMdxDirectiveDialogFormValues = {
  url?: string;
};

export type IYouTubeMdxDirectiveDialogProps = IOmit<
  IDialogProps,
  'headline' | 'actions' | 'children'
> & {
  onSubmit?: (
    values: IYouTubeMdxDirectiveDialogFormValues,
    event?: React.BaseSyntheticEvent,
  ) => void;
  defaultValues?: IYouTubeMdxDirectiveDialogFormValues;
};

const styles = stylex.create({
  host: {
    minWidth: 480,
  },
});

export const YouTubeMdxDirectiveDialogForm: React.FC<
  IYouTubeMdxDirectiveDialogProps
> = (props) => {
  const { sx, onSubmit, defaultValues, ...other } = props;
  const { t } = useTranslation();
  const form = useForm<IYouTubeMdxDirectiveDialogFormValues>({
    defaultValues,
  });
  const formId = useId();

  return (
    <Dialog
      {...other}
      sx={[styles.host, sx]}
      headline={t('YouTubeMdxDirectiveDialogForm.title')}
      actions={({ close }) => (
        <>
          <Button
            variant="text"
            onClick={close}
            disabled={form.formState.isSubmitting}
            form={formId}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={onSubmit ? form.handleSubmit(onSubmit) : undefined}
            form={formId}
          >
            {t('common.insert')}
          </Button>
        </>
      )}
    >
      <ContentBlock.FormBody
        id={formId}
        form={form}
        onSubmit={onSubmit}
        hideActionBar={true}
        extended
      >
        <FormSection>
          <FormField
            as={TextInputField}
            forwardError
            control={form.control}
            name="url"
            type="url"
            rules={{
              required: true,
              pattern: {
                value: /^https:\/\/www\.youtube\.com\/watch\?v=.+/,
                message: 'Invalid URL',
              },
            }}
          />
        </FormSection>
      </ContentBlock.FormBody>
    </Dialog>
  );
};
